import { useState, useEffect, useCallback } from "react";
import "./CRM.css"; // Reuse existing CRM styles
import dataService from "../services/dataService";
import { useToast } from "../context/ToastContext";
import { generateReportPDF } from "../utils/reportPDFGenerator";
import Modal from "../components/Modal";

function TeamPerformanceScreen() {
  const [performanceData, setPerformanceData] = useState([]);
  const [systemStats, setSystemStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("all_time");
  const [isTargetModalOpen, setIsTargetModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [targetValue, setTargetValue] = useState("");
  const { addToast } = useToast();

  const fetchPerformance = useCallback(async () => {
    try {
      setLoading(true);
      const response = await dataService.getTeamPerformanceReport(period);
      setPerformanceData(response.data.data);
      setSystemStats(response.data.systemStats);
    } catch (error) {
      console.error("Failed to fetch performance data", error);
      addToast("Failed to load performance metrics", "error");
    } finally {
      setLoading(false);
    }
  }, [period, addToast]);

  useEffect(() => {
    fetchPerformance();
  }, [fetchPerformance]);

  const totalTeamRevenue = performanceData.reduce((acc, curr) => acc + curr.totalRevenue, 0);

  const handleAction = (action, member) => {
    if (action === "Report generation") {
      generateReportPDF("teamPerformance", [member], {
        totalRevenue: member.totalRevenue,
        totalExpenses: member.totalRevenue * 0.7, // Estimated cost
        netProfit: member.totalProfit,
        taxPayable: member.totalRevenue * 0.05
      });
      addToast(`Performance report generated for ${member.name}`, "success");
    } else if (action === "Target setting") {
      setSelectedMember(member);
      setTargetValue("");
      setIsTargetModalOpen(true);
    } else if (action === "Encouragement") {
      addToast(`Encouragement and feedback sent to ${member.name}`, "success");
    }
  };

  const saveTarget = () => {
    addToast(`Target of AED ${targetValue} set for ${selectedMember?.name}`, "success");
    setIsTargetModalOpen(false);
  };

  return (
    <div className="crm-screen">
      <div className="screen-header" style={{ marginBottom: '24px' }}>
        <div className="header-info">
          <h1>Team Performance & AI Intelligence</h1>
          <p>Analyzing Human Efficiency vs System Throughput</p>
        </div>
        <div className="header-actions" style={{ display: 'flex', gap: '12px' }}>
          <select
            className="filter-select"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #E5E7EB', outline: 'none' }}
          >
            <option value="all_time">All Time</option>
            <option value="this_month">This Month</option>
            <option value="last_month">Last Month</option>
            <option value="last_7_days">Last 7 Days</option>
          </select>
          <button className="secondary-btn" onClick={fetchPerformance}>
            🔄 Sync Data
          </button>
        </div>
      </div>

      {/* Thinking in Systems: AI Intelligence Layer (Top Pulse) */}
      <div style={{
        background: 'linear-gradient(90deg, #1E1B4B 0%, #312E81 100%)',
        borderRadius: '12px',
        padding: '20px',
        color: 'white',
        marginBottom: '32px',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Subtle pulse background animation element can be added in CSS if needed */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <span style={{
              width: '10px', height: '10px', background: '#10B981', borderRadius: '50%',
              display: 'inline-block', boxShadow: '0 0 8px #10B981'
            }}></span>
            <span style={{ fontWeight: '600', fontSize: '14px', letterSpacing: '0.05em' }}>ERP SYSTEM ANALYTICS</span>
          </div>
          <h2 style={{ fontSize: '24px', margin: 0 }}>System Health: {systemStats?.systemHealth || 'Active'}</h2>
        </div>

        <div style={{ display: 'flex', gap: '40px', position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: '#A5B4FC', textTransform: 'uppercase' }}>Records Scanned</div>
            <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{systemStats?.totalRecordsProcessed?.toLocaleString() || 0}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: '#A5B4FC', textTransform: 'uppercase' }}>AI Observations</div>
            <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{((systemStats?.totalRecordsProcessed || 0) * 1.5).toFixed(0)}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: '#A5B4FC', textTransform: 'uppercase' }}>Current Load</div>
            <div style={{ fontSize: '20px', fontWeight: 'bold' }}>Low</div>
          </div>
        </div>

        {/* Decorative AI Icon */}
        <div style={{ position: 'absolute', right: '-20px', top: '-10px', opacity: '0.1', fontSize: '120px' }}>🤖</div>
      </div>

      <div className="summary-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
        {performanceData.slice(0, 3).map((performer, index) => (
          <div key={index} className="stat-card" style={{ borderLeft: `4px solid ${index === 0 ? '#F59E0B' : index === 1 ? '#94A3B8' : '#B45309'}` }}>
            <div className="stat-info">
              <span className="stat-label">
                {index === 0 ? "🏆 Monthly MVP" : index === 1 ? "🥈 Excellence" : "🥉 Strong Contributor"}
              </span>
              <h2 className="stat-value" style={{ fontSize: '20px', marginTop: '4px' }}>{performer.name}</h2>
              <p style={{ color: '#059669', fontWeight: '600', fontSize: '14px' }}>
                AED {performer.totalRevenue.toLocaleString()}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="content-card" style={{ marginTop: '32px' }}>
        <div className="card-controls" style={{ display: 'flex', justifyContent: 'space-between' }}>
          <h2 className="font-bold">Team Leaderboard</h2>
          <span style={{ fontSize: '12px', color: '#6B7280' }}>* Ranked by Revenue Contribution</span>
        </div>

        <div className="table-wrapper">
          {loading ? (
            <p style={{ padding: '20px' }}>Running recursive analytics...</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Member Info</th>
                  <th style={{ width: '250px' }}>Contribution Matrix</th>
                  <th>Orders</th>
                  <th>Value/Order</th>
                  <th>Profit</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {performanceData.map((member, index) => {
                  const contribution = totalTeamRevenue > 0 ? (member.totalRevenue / totalTeamRevenue) * 100 : 0;

                  return (
                    <tr key={index}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '36px', height: '36px', borderRadius: '8px', background: '#F3F4F6', color: '#4B5563',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold'
                          }}>
                            {member.name.substring(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-bold">{member.name}</div>
                            <div style={{ fontSize: '12px', color: '#6B7280' }}>
                              Sales Specialist
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{ flex: 1, height: '6px', background: '#F3F4F6', borderRadius: '3px', overflow: 'hidden' }}>
                            <div style={{
                              width: `${contribution}%`,
                              height: '100%',
                              background: contribution > 50 ? '#10B981' : contribution > 20 ? '#6366F1' : '#CBD5E1',
                              transition: 'width 1s ease-in-out'
                            }} />
                          </div>
                          <span style={{ fontSize: '12px', fontWeight: '700', minWidth: '40px' }}>{contribution.toFixed(1)}%</span>
                        </div>
                        <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '4px' }}>
                          AED {member.totalRevenue.toLocaleString()}
                        </div>
                      </td>
                      <td>
                        <span style={{ fontWeight: '600' }}>{member.orderCount}</span>
                      </td>
                      <td>
                        <div style={{ fontSize: '14px' }}>AED {member.avgOrderValue.toFixed(0)}</div>
                      </td>
                      <td>
                        <span style={{ color: '#059669', fontWeight: '700' }}>
                          AED {member.totalProfit.toLocaleString()}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                          <button
                             className="icon-btn"
                            title="Generate Performance Report"
                            onClick={() => handleAction("Report generation", member)}
                            style={{ padding: '6px', fontSize: '12px' }}
                          >
                            📈
                          </button>
                          <button
                            className="icon-btn success"
                            title="Set Target"
                            onClick={() => handleAction("Target setting", member)}
                            style={{ padding: '6px', fontSize: '12px' }}
                          >
                            🎯
                          </button>
                          <button
                            className="icon-btn"
                            title="Send Private Feedback"
                            onClick={() => handleAction("Encouragement", member)}
                            style={{ padding: '6px', fontSize: '12px' }}
                          >
                            💬
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {performanceData.length === 0 && (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                      No performance records found for this period.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal
        isOpen={isTargetModalOpen}
        onClose={() => setIsTargetModalOpen(false)}
        title={`Set Sales Target: ${selectedMember?.name}`}
        footer={
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button className="secondary-btn" onClick={() => setIsTargetModalOpen(false)}>Cancel</button>
            <button className="primary-btn" onClick={saveTarget}>Save Target</button>
          </div>
        }
      >
        <div style={{ padding: '20px 0' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
            Monthly Revenue Target (AED)
          </label>
          <input
            type="number"
            style={{ width: '100%', padding: '12px', border: '1px solid #E5E7EB', borderRadius: '8px', outline: 'none' }}
            placeholder="e.g. 50000"
            value={targetValue}
            onChange={(e) => setTargetValue(e.target.value)}
          />
          <p style={{ marginTop: '12px', fontSize: '12px', color: '#6B7280' }}>
            This target will be used to calculate efficiency and performance bonuses in future reports.
          </p>
        </div>
      </Modal>
    </div>
  );
}

export default TeamPerformanceScreen;
